/**
 * Resolve a marketing-site image URL.
 *
 * In dev (`VITE_CDN_BASE_URL` unset) returns `/images${path}` — Vite serves
 * the file directly from `public/images/` so dev works offline without any
 * CDN round-trip and without the BE running.
 *
 * In upper envs (`VITE_CDN_BASE_URL=https://cdn.homeplated.com/hp-www`)
 * returns the CDN URL, e.g. `https://cdn.homeplated.com/hp-www${path}`.
 * `dist/images/` is rsync'd to `gs://ffg-public-assets/hp-www/` at deploy
 * time (see `scripts/sync-cdn-assets.sh`), so the same `path` resolves on
 * both sides.
 *
 * **Bundle-pinned exceptions** — these stay hardcoded as `/images/...` and
 * do NOT route through this helper, because they're first-paint critical
 * and we want same-origin fetches regardless of env:
 *   - `SiteHeader.tsx` → `/images/logo-orange.svg`
 *   - HomePage hero `<img>` + `<source>` → `/images/photos/hero-friends-meal-*`
 *
 * **Stays under `/assets/`, not `/images/`:**
 *   - `public/assets/fonts/*.woff2`, referenced from `site.css` `@font-face`.
 *     Kept on the bundle origin because CSS `url()` can't read env vars and
 *     because a separate-origin font fetch is fiddly with the CSS
 *     `crossorigin` dance — not worth the move for ~50KB.
 *
 * @param path - path relative to the images root, MUST start with `/`
 *   (e.g. `"/photos/founder-peter.avif"`). The leading slash is required so
 *   the dev and CDN branches produce the same shape.
 */
export const assetUrl = (path: string): string => {
  const cdn = import.meta.env.VITE_CDN_BASE_URL || '';
  return cdn ? `${cdn}${path}` : `/images${path}`;
};
